import {
  ApiResponse,
  ClientBankAccountsLists,
  ClientCreditCardLists,
  DefaultPaymentMethodResponse,
  InvoicePdfRequest,
  InvoicePdfResponse,
  InvoicesRequest,
  PartnerInvoice,
  SetDefaultPaymentMethodRequest,
  SupportedCurrencies,
  Timezone,
} from 'vopay';

// What the host app supplies to the Account & Billing feature root, as props. Three kinds of
// thing, kept apart because they come from different places and mean different things:
//   viewer          — the person using the page (request metadata on billing calls)
//   account         — facts about the account whose billing is shown; identical for every user of it
//   userPermissions — what this particular user may do on it; differs by role
// Hosts map their own data model onto these at the mount; shared never sees a portal's own shape.

/** The person using the page — sent as Timezone on billing requests. */
export type BillingViewer = {
  username: string;
  timezone: Timezone;
};

/** Facts about the account whose billing is on screen. */
export type BillingAccount = {
  /**
   * ISO-3166 alpha-2. 'CA' and 'US' get their domestic routing details prefixed onto displayed
   * account numbers; every other value — including the empty string the client portal's ClientData
   * holds before the account loads — renders the account number bare. Deliberately not narrowed to
   * 'CA' | 'US': the third case is reachable and handled.
   */
  country: string;
  /** Franchise sub-account: drops the "and withdrawals" copy, requires the PAD agreement when adding a billing account */
  isFranchise: boolean;
  /** Features VoPay has switched on for the account — entitlements, not user permissions */
  features: {
    /** Credit cards may be billing methods; otherwise the option is locked and cards aren't fetched */
    creditCard: boolean;
    /** "Add billing method" opens the Elinx bank-connect flow instead of the pick-an-existing-account dialog */
    elinxDirectAdd: boolean;
  };
};

/** What this user may do on the account. */
export type BillingUserPermissions = {
  canManageBilling: boolean;
  /** Whether the Add Billing Method flow is offered at all. Partners can't add billing methods. */
  canAddBilling: boolean;
};

/**
 * The second billing tab's variant — which downloads it offers and how it is labelled. Clients get the
 * fused invoices+statements tab; a partner gets one or the other depending on partner type.
 */
export type BillingSecondTab = 'invoiceAndStatement' | 'invoice' | 'statement';

/** A downloaded statement. Clients get a CSV, partners a PDF, so the format travels with the bytes. */
export type DownloadedStatement = ApiResponse & {
  data: string;
  format: 'csv' | 'pdf';
};

/** Normalized invoice-list result — the account and partner responses spell the list differently. */
export type BillingInvoicesResult = ApiResponse & {
  Invoices: PartnerInvoice[];
};

/**
 * The billing calls whose endpoint differs per host (account vs partner). The host builds these from
 * its own injected api and passes them to the feature root; shared calls them without knowing which
 * portal it is in — the (c) interface. Everything else the page fetches (bank accounts, credit cards)
 * hits the same URL for every host, so it stays on useApi() and is not here.
 */
export type BillingApi = {
  fetchInvoices: (params: InvoicesRequest) => Promise<BillingInvoicesResult>;
  fetchInvoicePdf: (params: InvoicePdfRequest) => Promise<InvoicePdfResponse>;
  fetchStatement: (params: { InvoiceID: string }) => Promise<DownloadedStatement>;
  fetchDefaultPaymentMethod: () => Promise<DefaultPaymentMethodResponse>;
  setDefaultPaymentMethod: (params: SetDefaultPaymentMethodRequest) => Promise<ApiResponse>;
};

export type AccountAndBillingHostProps = {
  viewer: BillingViewer;
  account: BillingAccount;
  userPermissions: BillingUserPermissions;
  /** The host-varying billing calls (account vs partner endpoints). */
  billingApi: BillingApi;
  /** Which second tab to render; defaults to the client's fused invoices+statements. */
  secondTab?: BillingSecondTab;
};

// Account-row types shared by the Account & Billing and Operational Accounts listings.

export type MyAccountType = 'Bank Account' | 'Credit Card';

/**
 * The default flags a row carries. `IsDefault` is the flag the listing itself renders ("this is the
 * default here"); the other two describe the row for the details drawer, which shows both defaults
 * regardless of which listing opened it — and are absent on rows belonging to the other purpose,
 * which are only ever offered as accounts to add.
 */
export type AccountRowDefaults = {
  IsDefault: boolean;
  IsOperationalDefault?: boolean;
  IsBillingDefault?: boolean;
};

type AccountRowCommon = AccountRowDefaults & {
  /** Stamped by buildAccountRows — neither list response carries a country. */
  Country: string;
  /** The backend's own "default" string on the account record, as a boolean. */
  IsDefaultAccount?: boolean;
};

/**
 * A bank-account row: the list response as it arrives, plus what buildAccountRows stamps on. Derived
 * from `ClientBankAccountsLists` rather than restated field by field, so a response field that gets
 * added, removed or respelled can't silently stop reaching the row (which is exactly how `BankID`
 * went unread as `BankId` for years).
 */
export type BankAccountRow = Omit<ClientBankAccountsLists, 'IsDefault' | 'Province/State' | 'PostalCode/ZipCode'> &
  AccountRowCommon & {
    Type: 'Bank Account';
    /** The response spells these two with a slash; rows don't — see buildAccountRows. */
    Province?: string;
    PostalCode?: string;
    /** The response's `BankID`, remapped — the logo lookup and the row both want one spelling. */
    BankId?: string;
    /** Only set on rows sourced from a default-account response, which names the recipient instead. */
    RecipientName?: string;
  };

/** A credit-card row. Cards belong to no Purpose and VoPay only accepts CAD, which the response omits. */
export type CreditCardRow = Omit<ClientCreditCardLists, 'IsDefault'> &
  AccountRowCommon & {
    Type: 'Credit Card';
    Currency: SupportedCurrencies;
  };

/**
 * A row in either listing. Discriminated on `Type` so the identifier is guaranteed: a bank row always
 * has `Token`, a card row always has `CreditCardToken`, and there is no row without one. The previous
 * shape made both optional, which forced an id fallback of `''` — two id-less rows then collided as
 * React keys and the table reconciled the wrong row.
 */
export type AccountRow = BankAccountRow | CreditCardRow;

// The listing filters, as they live in the URL.

/** A row `Type` with its spaces removed: `'Bank Account'` becomes `'BankAccount'`. */
type CompactAccountType<T extends string> = T extends `${infer Head} ${infer Tail}`
  ? `${Head}${CompactAccountType<Tail>}`
  : T;

/**
 * The type filter's URL values, and the single list the Select renders from. Compact rather than the
 * row's own `'Bank Account'`/`'Credit Card'` because a bookmarked `?type=BankAccount` has to keep
 * working. The `satisfies` is what ties the two vocabularies together: a filter value with no row
 * `Type` behind it fails here, on the value itself, and renaming a row `Type` fails here too rather
 * than silently orphaning the bookmarked URL.
 */
export const ACCOUNT_TYPE_FILTER_OPTIONS = [
  'BankAccount',
  'CreditCard',
] as const satisfies readonly CompactAccountType<MyAccountType>[];

export type AccountTypeFilter = (typeof ACCOUNT_TYPE_FILTER_OPTIONS)[number];

/** The currencies the filter offers, of the nine vopay supports. Also the Select's options. */
export const CURRENCY_FILTER_OPTIONS = ['CAD', 'USD'] as const satisfies readonly SupportedCurrencies[];

export type CurrencyFilter = (typeof CURRENCY_FILTER_OPTIONS)[number];

/** `''` is "no filter in the URL"; `'All'` is the option the user picked explicitly. Both match every row. */
export type AccountFilterValue<T extends string> = '' | 'All' | T;

export type AccountSearchParams = {
  type: AccountFilterValue<AccountTypeFilter>;
  currency: AccountFilterValue<CurrencyFilter>;
  searchTerm: string;
};

export type InvoicesSearchParams = {
  searchTerm: string;
};

/** The row `Type` each type-filter value selects. Exhaustive by construction. */
export const ACCOUNT_TYPE_BY_FILTER: Record<AccountTypeFilter, MyAccountType> = {
  BankAccount: 'Bank Account',
  CreditCard: 'Credit Card',
};

/** Narrows arbitrary URL text to a filter value, so a hand-edited `?type=banana` reads as "no filter". */
export function toAccountTypeFilter(value: string | null): AccountFilterValue<AccountTypeFilter> {
  if (value === 'All') return 'All';

  return ACCOUNT_TYPE_FILTER_OPTIONS.find((option) => option === value) ?? '';
}

/** As above for the currency filter. */
export function toCurrencyFilter(value: string | null): AccountFilterValue<CurrencyFilter> {
  if (value === 'All') return 'All';

  return CURRENCY_FILTER_OPTIONS.find((option) => option === value) ?? '';
}
